import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Languages, Loader2, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("credentials"); // credentials | mfa
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await login(email, password);
      if (res && res.challenge) { setStage("mfa"); }
      else navigate("/dashboard");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await verifyMfa(code.trim());
      navigate("/dashboard");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex sky-bg">
      <button onClick={toggle} className="absolute top-5 right-5 z-10 flex items-center gap-1.5 text-white/90 text-sm font-medium">
        <Languages className="h-4 w-4" /> {lang.toUpperCase()}
      </button>

      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 brand-panel text-white">
        <img src="/inzeair-logo.jpg" alt="InZeAir by Académie Bel-Air" className="w-56 rounded-2xl shadow-2xl ring-1 ring-white/15" data-testid="login-logo" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-display font-extrabold text-5xl leading-tight tracking-tight drop-shadow-sm">
            L'académie,<br /><span style={{ color: "#E7C15A" }}>vue du ciel.</span>
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-md">{t("loginTagline")}</p>
        </motion.div>
        <div className="text-white/60 text-sm">© {new Date().getFullYear()} InZeAir · Académie Bel-Air</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <Card className="bg-white p-8 rounded-2xl border-0 shadow-2xl">
            <div className="lg:hidden mb-6 flex justify-center"><img src="/inzeair-logo.jpg" alt="InZeAir" className="w-40 rounded-xl" /></div>

            {stage === "credentials" && (
              <>
                <h2 className="font-display font-bold text-2xl text-slate-900">{t("login")}</h2>
                <p className="text-sm text-slate-500 mb-6">InZeAir · Académie Bel-Air</p>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-slate-600">{t("email")}</label>
                    <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      required placeholder="vous@exemple.com" />
                  </div>
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-slate-600">{t("password")}</label>
                    <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
                  </div>
                  {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
                  <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full rounded-full h-11 text-base">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("signIn")}
                  </Button>
                  <div className="text-center">
                    <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-primary hover:underline">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                </form>
              </>
            )}

            {stage === "mfa" && (
              <>
                <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-5 w-5 text-primary" />
                  <h2 className="font-display font-bold text-2xl text-slate-900">Vérification en deux étapes</h2></div>
                <p className="text-sm text-slate-500 mb-6">Saisissez le code à 6 chiffres de votre application d'authentification (ou un code de récupération).</p>
                <form onSubmit={submitMfa} className="space-y-4">
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-slate-600">Code de vérification</label>
                    <Input data-testid="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} required
                      placeholder="123456" autoFocus className="tracking-widest text-center text-lg" />
                  </div>
                  {error && <div data-testid="mfa-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
                  <Button data-testid="mfa-submit" type="submit" disabled={busy} className="w-full rounded-full h-11 text-base">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vérifier"}
                  </Button>
                  <button type="button" onClick={() => { setStage("credentials"); setCode(""); setError(""); }}
                    className="w-full text-sm text-slate-500 hover:underline">Retour</button>
                </form>
              </>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
